import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Dialog,
    DialogBody,
    DialogSurface,
    DialogTitle,
    DialogContent,
    DialogTrigger,
    Field,
    Input,
} from '@fluentui/react-components';
import {
    DialogActions,
    DialogOpenChangeEventHandler,
} from '@fluentui/react-dialog';
import { Dismiss24Regular } from '@fluentui/react-icons';

export enum CategoryDialogMode {
    New = 'New',
    Edit = 'Edit',
}

interface CreateOrUpdateCategoryDialogActionProps {
    open: boolean;
    mode: CategoryDialogMode;
    existingCategoryTitles: string[];
    categoryTitle: string;
    categoryKey: string;
    onSaveBtnClick: (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        data: {
            categoryKey: string;
            newCategoryTitle: string;
        }
    ) => void;
    onOpenChange?: DialogOpenChangeEventHandler;
}

const getCategoryValidationError = (
    categoryName: string,
    newCategoryName: string,
    maxCategoryNameLength: number,
    t: any,
    existingCategoryTitles: string[],
    mode: CategoryDialogMode
): string | undefined => {
    if (newCategoryName.length > maxCategoryNameLength) {
        return t('CreateOrUpdateCategoryDialog_MaxLength_Error').replace(
            '{maxLength}',
            maxCategoryNameLength.toString()
        );
    }
    if (newCategoryName.trim().length === 0) {
        return t('CreateOrUpdateCategoryDialog_Blank_Error');
    }
    // Azure Table Storage forbidden characters: / \ # ? and control chars U+0000-U+001F, U+007F-U+009F (Horizontal tab (\t) Linefeed (\n) Carriage return (\r))
    const forbiddenPattern = /[\/\\#\?\u0000-\u001F\u007F-\u009F]/; // eslint-disable-line no-control-regex, no-useless-escape
    if (forbiddenPattern.test(newCategoryName)) {
        return t('CreateOrUpdateCategoryDialog_InvalidChars_Error');
    }
    if (
        existingCategoryTitles.some(
            (title) =>
                title.trim().toLowerCase() ===
                    newCategoryName.trim().toLowerCase() &&
                (mode === CategoryDialogMode.New ||
                    title.trim().toLowerCase() !==
                        categoryName.trim().toLowerCase())
        )
    ) {
        return t('CreateOrUpdateCategoryDialog_Duplicate_Error');
    }
    return undefined;
};

const CreateOrUpdateCategoryDialogAction = ({
    open,
    mode,
    existingCategoryTitles,
    categoryTitle,
    categoryKey,
    onSaveBtnClick,
    onOpenChange,
}: CreateOrUpdateCategoryDialogActionProps) => {
    const { t } = useTranslation();
    const [newCategoryName, setNewCategoryName] = useState<string>('');
    const maxCategoryNameLength = 128;

    useEffect(() => {
        if (mode === CategoryDialogMode.Edit) {
            setNewCategoryName(categoryTitle);
        }
    }, [mode, categoryTitle]);

    useEffect(() => {
        if (!open) {
            setNewCategoryName('');
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle
                        action={
                            <DialogTrigger action="close">
                                <Button
                                    appearance="subtle"
                                    aria-label="close"
                                    icon={<Dismiss24Regular />}
                                />
                            </DialogTrigger>
                        }
                    >
                        {t('CreateOrUpdateCategoryDialog_Title').replace(
                            '{mode}',
                            mode
                        )}
                    </DialogTitle>

                    <DialogContent>
                        <Field
                            label={t(
                                'CreateOrUpdateCategoryDialog_CategoryField_Label'
                            )}
                            required
                            validationState={
                                newCategoryName.length > 0 &&
                                getCategoryValidationError(
                                    categoryTitle,
                                    newCategoryName,
                                    maxCategoryNameLength,
                                    t,
                                    existingCategoryTitles,
                                    mode
                                )
                                    ? 'error'
                                    : undefined
                            }
                            validationMessage={
                                newCategoryName.length > 0
                                    ? getCategoryValidationError(
                                          categoryTitle,
                                          newCategoryName,
                                          maxCategoryNameLength,
                                          t,
                                          existingCategoryTitles,
                                          mode
                                      )
                                    : undefined
                            }
                        >
                            <Input
                                type="text"
                                placeholder={
                                    t(
                                        'CreateOrUpdateCategoryDialog_CategoryField_Placeholder'
                                    ) ?? ''
                                }
                                value={newCategoryName}
                                onChange={(_, data) => {
                                    setNewCategoryName(data.value);
                                }}
                            />
                        </Field>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            appearance="primary"
                            disabled={
                                !!getCategoryValidationError(
                                    categoryTitle,
                                    newCategoryName,
                                    maxCategoryNameLength,
                                    t,
                                    existingCategoryTitles,
                                    mode
                                )
                            }
                            onClick={(e) => {
                                onSaveBtnClick(e, {
                                    categoryKey: categoryKey,
                                    newCategoryTitle: newCategoryName,
                                });
                            }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default CreateOrUpdateCategoryDialogAction;
